import { modalizeWatchdogFilter, type WatchdogCountData } from 'service/watchdog/model.ts';
import { cn } from 'utils/cn.tsx';
import { categoriesMap } from 'signals/category';
import { Icon } from 'components/atoms/Icon';
import { Dropdown } from 'components/dropdown/Dropdown.tsx';
import { localize, pluralize } from 'i18n/localize.ts';
import { useSignal } from '@preact/signals';
import { WatchdogModal } from 'components/modal/watchdog/WatchdogModal.tsx';
import { useCallback } from 'preact/hooks';
import { removeWatchdog } from 'service/watchdog/resource';
import { toastMessages } from 'components/toastMessage/signals.ts';
import { number } from 'utils/format';
import { urlEncoder } from 'utils/url.ts';
import { watchdogFilterToUrlFilterParams } from 'utils/watchdogUrl.ts';
import { useSignals } from 'context/SignalsContext';
import { getWatchdogInfoLine } from 'components/watchdog/utils.tsx';
import { SessionStorage } from 'utils/SessionStorage.ts';
import {
	sendDeleteSavedSearchSearchAnalytics,
	sendOpenSavedSearchModalAnalytics,
} from 'lib/dot/savedSearches/utils.ts';
import { SearchType } from 'lib/dot/enum/searchType.ts';
import { DOTHelper } from 'lib/dot/DOTHelper.ts';

export type Props = {
	watchdog: WatchdogCountData;
	className?: string;
};

const OPTIONS = [
	{ id: 'edit', icon: 'edit' },
	{ id: 'delete', icon: 'delete' },
] as const;

function renderOpener({ aria, onKeyDown, toggleOpen, openerRef }: Dropdown.RenderOpenerProps) {
	return (
		<button ref={openerRef} onClick={toggleOpen} {...aria} onKeyDown={onKeyDown} class="block">
			<Icon className="text-dark-blue-25 hover:text-dark-blue block" type="more_horiz" size={24} />
		</button>
	);
}

function renderOption({ props, option, aria, id, isFocused }: Dropdown.RenderOptionProps<(typeof OPTIONS)[number]>) {
	return (
		<button
			className={cn(
				'flex items-center gap-4 w-full text-left py-2 text-light-blue hover:text-dark-blue focus:text-dark-blue cursor-pointer',
				isFocused ? 'text-dark-blue' : '',
			)}
			{...props}
			{...aria}
			id={id}
		>
			<Icon type={option.icon} /> {$t.watchdog.dropdown[option.id]}
		</button>
	);
}

const $t = localize();

export function WatchdogItem({ watchdog, className }: Props) {
	const { watchdogs } = useSignals(['watchdogs']);
	const modalOpen = useSignal(false);
	const isDeleting = useSignal(false);

	const onOptionSelected: Dropdown.OptionSelectedHandler<(typeof OPTIONS)[number]> = useCallback(
		async (event, option) => {
			event.preventDefault();

			if (isDeleting.value) return;

			if (option.id === 'delete') {
				try {
					isDeleting.value = true;
					await removeWatchdog(watchdog.id);
					sendDeleteSavedSearchSearchAnalytics(watchdog.filter, watchdog.notificationInterval);
					toastMessages.addMessage($t.watchdog.toastDeleteSuccess, 'success');
					watchdogs.remove(watchdog.id);
				} catch (e) {
					console.error(e);
					toastMessages.addMessage($t.watchdog.toastDeleteError, 'error');
				} finally {
					isDeleting.value = false;
				}
			} else if (option.id === 'edit') {
				sendOpenSavedSearchModalAnalytics(watchdog.filter);
				modalOpen.value = true;
			}
		},
		[watchdog],
	);

	const hasNewOffers = watchdog.newCount > 0;

	let topLine = '';
	let bottomLinePrefix = '';
	const categoryText =
		(watchdog.filter.categoryId &&
			(categoriesMap.value[watchdog.filter.categoryId]?.name ?? $t.categories.unknown)) ||
		$t.categories.allCategories;

	if (watchdog.filter.phrase) {
		topLine = `„${watchdog.filter.phrase}“`;
		bottomLinePrefix = categoryText;
	} else {
		topLine = categoryText;
	}

	return (
		<>
			<a
				href={urlEncoder(watchdogFilterToUrlFilterParams(watchdog))}
				onClick={() => {
					SessionStorage.setSearchMeta({
						searchType: SearchType.SAVED_SEARCH,
						tab: DOTHelper.getStore().tab,
					});
				}}
			>
				<div
					className={cn(
						'group rounded-xl flex flex-col bg-gray-dark-background hover:bg-gray-light-background md:flex-row pl-6 pr-8 py-4 md:items-center gap-1 md:gap-0 relative md:static leading-6 text-sm text-dark-blue-70',
						className,
					)}
				>
					<div class="absolute left-4 top-5 md:static group-hover:text-dark-blue-50 text-dark-blue-25 mr-4">
						<Icon type={'search'} size={28} />
					</div>
					<div className="ml-7 md:ml-0 flex flex-col flex-grow-0 gap-1 md:gap-0 group-hover:text-dark-blue">
						<div class="font-bold">{topLine}</div>
						{
							<div>
								{bottomLinePrefix} {getWatchdogInfoLine(watchdog)}
							</div>
						}
					</div>
					<div
						class={cn('flex-grow flex md:justify-end mr-4 md:mr-6 ml-7 md:ml-0', {
							'text-neutral-white hover:text-neutral-white': hasNewOffers,
							'text-red hover:text-red': !hasNewOffers,
						})}
					>
						<span
							className={cn(
								'flex items-center justify-end leading-6',
								hasNewOffers
									? 'bg-red-100 hover:bg-red-hover focus:bg-red-focus rounded-full mt-4 md:mt-0 px-4 py-0.5'
									: 'rounded-2xl hover:bg-red-10',
							)}
						>
							{hasNewOffers ? (
								pluralize(watchdog.newCount, $t.watchdog.newOfferCount, {
									count: number(watchdog.newCount),
								})
							) : (
								<span>
									<span class="md:hidden">Zobrazit </span>
									{pluralize(watchdog.count || 0, $t.watchdog.offerCount, {
										count: number(watchdog.count),
									})}
								</span>
							)}
						</span>
					</div>
					<div class="flex-grow-0 absolute md:static right-4 top-4 md:block">
						<Dropdown
							className={'md:w-[230px]'}
							renderOpener={renderOpener}
							renderOption={renderOption}
							options={OPTIONS}
							onOptionSelected={onOptionSelected}
							optionAriaRole="menuitem"
							containerAriaRole="menu"
							openerAriaLabel={$t.watchdog.ariaLabel}
							idPrefix={`watchdog-${watchdog.id}-actions`}
						/>
					</div>
				</div>
			</a>
			{modalOpen.value && (
				<WatchdogModal
					initialData={{
						filter: modalizeWatchdogFilter(watchdog.filter),
						notification_interval: watchdog.notificationInterval,
						use_seznam_bar: true,
					}}
					editId={watchdog.id}
					onModalClose={() => {
						modalOpen.value = false;
					}}
					onWatchdogDelete={() => {
						watchdogs.remove(watchdog.id);
					}}
					onWatchdogUpdated={async (id) => {
						await watchdogs.refreshWatchdogRemotely(id);
					}}
				/>
			)}
		</>
	);
}
